import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchImagesByMaterialForHomePage, fetchMaterialIconsByProductIds, persistProductSelection } from '../services';
import { 
  updateSelectedMaterial, 
  updateSelectedMaterialInLiked, 
  updateSelectedMaterialInType 
} from '../redux';
import { 
  makeSelectImageStatusByProductId, 
  makeSelectMaterialIconsByProductId, 
  makeSelectProductImagesByProductId 
} from '../redux/createSelector';
import { IProductsCard } from '@/components/interfaces';

export const useProductCardData = (product: IProductsCard, loadMaterialIcons = true) => {
  const { id: productId, selectedMaterial } = product;
  const dispatch = useAppDispatch();
  const location = useLocation();
  const userId = useAppSelector((state) => state.user.user?.id);

  const [modalVisible, setModalVisible] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [displayImages, setDisplayImages] = useState<string[]>([]);

  const source = useMemo<'home' | 'liked' | 'types'>(() => {
    if (location.pathname.includes('/liked')) return 'liked';
    if (location.pathname.startsWith('/products/types/')) return 'types';
    return 'home';
  }, [location.pathname]);

  useEffect(() => {
    if (productId && loadMaterialIcons) {
      dispatch(fetchMaterialIconsByProductIds({ ids: [productId], source }));
    }
  }, [productId, source, dispatch, loadMaterialIcons]);

  const productImagesSelector = useMemo(() => makeSelectProductImagesByProductId(location.pathname), [location.pathname]);
  const imageStatusSelector = useMemo(() => makeSelectImageStatusByProductId(location.pathname), [location.pathname]);
  const materialIconsSelector = useMemo(() => makeSelectMaterialIconsByProductId(location.pathname), [location.pathname]);

  const images = useAppSelector(productImagesSelector);
  const imageStatus = useAppSelector((state) => imageStatusSelector(state)[productId] || 'idle');
  const materialIcons = useAppSelector(materialIconsSelector);
  const cachedHomeImageEntry = useAppSelector((state) =>
    source === 'home' ? state.products.imagesByMaterialForHomePage[productId] : undefined
  );

  const imagesForProduct = images[productId] as { images?: string[] } | string[] | undefined;
  const rawImages = useMemo(() => {
    return Array.isArray(imagesForProduct) ? imagesForProduct : imagesForProduct?.images ?? [];
  }, [imagesForProduct]);
  const hasCachedImagesForSelectedMaterial =
    source === 'home' &&
    cachedHomeImageEntry?.selectedMaterial === selectedMaterial &&
    Array.isArray(cachedHomeImageEntry.images) &&
    cachedHomeImageEntry.images.length > 0;

  useEffect(() => {
    if (productId && selectedMaterial && !hasCachedImagesForSelectedMaterial) {
      setImageIndex(0);
      setDisplayImages([]);
      dispatch(fetchImagesByMaterialForHomePage({ productId, selectedMaterial, source }));
    }
  }, [productId, selectedMaterial, source, dispatch, hasCachedImagesForSelectedMaterial]);

  useEffect(() => {
    if (rawImages.length) {
      setDisplayImages(rawImages.slice(0, 2));
      setImageIndex(0);
    }
  }, [rawImages]);

  const displayImage = displayImages[imageIndex];

  const handleMaterialSelect = (materialName: string) => {
    if (materialName === selectedMaterial) return;

    if (source === 'home') {
      dispatch(updateSelectedMaterial({ productId, materialName }));
    } else if (source === 'liked') {
      dispatch(updateSelectedMaterialInLiked({ productId, materialName }));
    } else {
      dispatch(updateSelectedMaterialInType({ productId, materialName }));
    }

    void dispatch(persistProductSelection({ productId, userId, selectedMaterial: materialName }))
      .unwrap()
      .catch((err) => console.error('Error updating material:', err));
  };
  

  return {
    modalVisible,
    setModalVisible,
    imageStatus,
    displayImage,
    imageIndex,
    displayImages,
    setImageIndex,
    handleMaterialSelect,
    materialIcons: loadMaterialIcons ? materialIcons[productId] : undefined,
  };
};
